import { useState, useEffect, useRef } from "react";
import {
  ActionIcon, Alert, Anchor, Badge, Button, Card, Center, Container, Group, Loader, Modal, Pagination, Stack,
  Table, Text, TextInput, Textarea, Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { IconEdit, IconGitMerge, IconPlus, IconSearch, IconWorld } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { getCompanies, searchCompanies, createCompany, updateCompany, getCompanyDedupPreview, startCompanyDedup } from "../api";
import { CompanyLogo } from "../Candidates/CompanyLogo";
import { useLocation } from "wouter";

export default function AdminCompaniesPage() {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [companies, setCompanies] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editCompany, setEditCompany] = useState(null);
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);
  const pageSize = 50;

  const createForm = useForm({
    initialValues: { name: "", location: "", industry: "", website: "", linkedInUrl: "", aliases: "" },
    validate: { name: (v) => (v.trim() ? null : t("name") + " required") },
  });

  const editForm = useForm({
    initialValues: { name: "", location: "", industry: "", website: "", linkedInUrl: "", aliases: "" },
  });

  useEffect(() => { loadCompanies(); }, [page]);

  async function loadCompanies() {
    setLoading(true);
    try {
      const data = await getCompanies(page, pageSize);
      setCompanies(data?.data ?? []);
      setTotalCount(data?.totalCount ?? 0);
    } catch (e) {
      console.error("Failed to load companies", e);
    } finally {
      setLoading(false);
    }
  }

  function handleSearchChange(e) {
    const q = e.currentTarget.value;
    setSearch(q);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!q.trim()) {
      debounceRef.current = setTimeout(() => { setPage(1); loadCompanies(); }, 100);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const results = await searchCompanies(q);
        setCompanies(Array.isArray(results) ? results : []);
        setTotalCount(Array.isArray(results) ? results.length : 0);
      } catch { setCompanies([]); setTotalCount(0); }
      finally { setLoading(false); }
    }, 300);
  }

  async function handleCreate(values) {
    setSaving(true);
    try {
      const dto = { name: values.name.trim() };
      if (values.location.trim()) dto.location = values.location.trim();
      if (values.industry.trim()) dto.industry = values.industry.trim();
      if (values.website.trim()) dto.website = values.website.trim();
      if (values.linkedInUrl.trim()) dto.linkedInUrl = values.linkedInUrl.trim();
      if (values.aliases.trim()) dto.aliases = values.aliases.split(",").map((a) => a.trim()).filter(Boolean);
      const company = await createCompany(dto);
      setCreateOpen(false);
      createForm.reset();
      notifications.show({ title: t("create"), message: company.name });
      loadCompanies();
    } catch (e) {
      const msg = e?.response?.status === 409 ? t("company_exists") : t("error");
      notifications.show({ color: "red", title: t("error"), message: msg });
    } finally {
      setSaving(false);
    }
  }

  function startEdit(company) {
    setEditCompany(company);
    editForm.setValues({
      name: company.name ?? "",
      location: company.location ?? "",
      industry: company.industry ?? "",
      website: company.website ?? "",
      linkedInUrl: company.linkedInUrl ?? "",
      aliases: (company.aliases ?? []).join(", "),
    });
  }

  async function handleUpdate(values) {
    if (!editCompany) return;
    setSaving(true);
    try {
      const dto = {
        name: values.name.trim() || editCompany.name,
        location: values.location.trim() || null,
        industry: values.industry.trim() || null,
        website: values.website.trim() || null,
        linkedInUrl: values.linkedInUrl.trim() || null,
        aliases: values.aliases.trim() ? values.aliases.split(",").map((a) => a.trim()).filter(Boolean) : [],
      };
      const updated = await updateCompany(editCompany.id, dto);
      setCompanies((prev) => prev.map((c) => (c.id === editCompany.id ? updated : c)));
      setEditCompany(null);
      notifications.show({ title: t("save"), message: updated.name });
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("error") });
    } finally {
      setSaving(false);
    }
  }

  const totalPages = Math.ceil(totalCount / pageSize);

  return (
    <Container size="md" py="md">
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={3}>{t("companies")}</Title>
          <Text size="sm" c="dimmed">{t("companies_count", { count: totalCount })}</Text>
        </Group>
        <Button leftSection={<IconPlus size={16} />} size="sm" onClick={() => setCreateOpen(true)}>
          {t("add_company")}
        </Button>
      </Group>

      <CompanyDedupPanel onMerged={loadCompanies} />

      <TextInput
        placeholder={t("search_companies")}
        leftSection={<IconSearch size={16} />}
        value={search}
        onChange={handleSearchChange}
      />

      {loading ? (
        <Center py="xl"><Loader /></Center>
      ) : companies.length === 0 ? (
        <Text c="dimmed" ta="center" py="xl">{t("no_companies")}</Text>
      ) : (
        <>
          <Table.ScrollContainer minWidth={600}>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("name")}</Table.Th>
                  <Table.Th>{t("industry")}</Table.Th>
                  <Table.Th>{t("location")}</Table.Th>
                  <Table.Th>{t("aliases")}</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {companies.map((c) => {
                  // Prefer a manually-set LinkedIn URL; otherwise derive one from the captured company id
                  // (linkedin.com/company/<numericId> resolves to the real page). Display-only — we don't
                  // persist the derived URL, so the LinkedInUrl field stays free for a real value.
                  const liUrl = c.linkedInUrl || (c.linkedInCompanyId ? `https://www.linkedin.com/company/${c.linkedInCompanyId}` : null);
                  return (
                  <Table.Tr key={c.id}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <CompanyLogo companyId={c.id} companyName={c.name} size={24} color="gray" style={{ flexShrink: 0 }} />
                        <div style={{ minWidth: 0 }}>
                          <Anchor size="sm" fw={500} style={{ cursor: "pointer" }}
                            onClick={() => setLocation(`/companies/${c.id}`)}>{c.name}</Anchor>
                          {c.website && (
                            <Text size="xs" c="dimmed" truncate>
                              <a href={c.website.startsWith("http") ? c.website : `https://${c.website}`} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
                                {c.website}
                              </a>
                            </Text>
                          )}
                          {liUrl && (
                            <Text size="xs" c="dimmed" truncate>
                              <a href={liUrl} target="_blank" rel="noopener noreferrer" style={{ color: "inherit" }}>
                                {c.linkedInCompanyId ? `LinkedIn · ${c.linkedInCompanyId}` : "LinkedIn"}
                              </a>
                            </Text>
                          )}
                        </div>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{c.industry ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">{c.location ?? "—"}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        {(c.aliases ?? []).slice(0, 3).map((a, i) => (
                          <Badge key={i} size="xs" variant="light">{a}</Badge>
                        ))}
                        {(c.aliases ?? []).length > 3 && (
                          <Badge size="xs" variant="light" color="gray">+{c.aliases.length - 3}</Badge>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <ActionIcon variant="subtle" size="sm" onClick={() => startEdit(c)}>
                        <IconEdit size={14} />
                      </ActionIcon>
                    </Table.Td>
                  </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>

          {!search && totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </>
      )}

      {/* Create Modal */}
      <Modal opened={createOpen} onClose={() => setCreateOpen(false)} title={t("add_company")}>
        <form onSubmit={createForm.onSubmit(handleCreate)}>
          <Stack>
            <TextInput label={t("company_name")} required {...createForm.getInputProps("name")} />
            <TextInput label={t("industry")} {...createForm.getInputProps("industry")} />
            <TextInput label={t("location")} {...createForm.getInputProps("location")} />
            <TextInput label={t("website")} placeholder="www.example.com" {...createForm.getInputProps("website")} />
            <TextInput label={t("linkedin_url")} placeholder="linkedin.com/company/..." {...createForm.getInputProps("linkedInUrl")} />
            <TextInput label={t("aliases")} placeholder={t("aliases_placeholder")} {...createForm.getInputProps("aliases")} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setCreateOpen(false)}>{t("cancel")}</Button>
              <Button type="submit" loading={saving}>{t("create")}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal opened={!!editCompany} onClose={() => setEditCompany(null)} title={editCompany?.name ?? ""}>
        <form onSubmit={editForm.onSubmit(handleUpdate)}>
          <Stack>
            <TextInput label={t("company_name")} required {...editForm.getInputProps("name")} />
            <TextInput label={t("industry")} {...editForm.getInputProps("industry")} />
            <TextInput label={t("location")} {...editForm.getInputProps("location")} />
            <TextInput label={t("website")} {...editForm.getInputProps("website")} />
            <TextInput label={t("linkedin_url")} {...editForm.getInputProps("linkedInUrl")} />
            <TextInput label={t("aliases")} placeholder={t("aliases_placeholder")} {...editForm.getInputProps("aliases")} />
            <Group justify="flex-end">
              <Button variant="default" onClick={() => setEditCompany(null)}>{t("cancel")}</Button>
              <Button type="submit" loading={saving}>{t("save")}</Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </Stack>
    </Container>
  );
}

function jobIsActive(job) {
  const status = job?.status;
  return status === "queued" || status === "processing" || status === 0 || status === 10;
}

function CompanyDedupPanel({ onMerged }) {
  const { t } = useTranslation("Veft");
  const [, setLocation] = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const runningMerge = useRef(false);

  async function load() {
    try {
      const next = await getCompanyDedupPreview();
      setData(next);
      return next;
    } catch {
      notifications.show({ color: "red", title: t("error"), message: t("company_dedup_load_failed") });
      return null;
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  const job = data?.job;
  const merging = jobIsActive(job) && job?.merge;
  useEffect(() => {
    if (!merging) return undefined;
    runningMerge.current = true;
    const tmr = setInterval(async () => {
      const next = await load();
      if (jobIsActive(next?.job) && next?.job?.merge) return;
      clearInterval(tmr);
      if (runningMerge.current) {
        runningMerge.current = false;
        notifications.show({ color: "green", message: t("company_dedup_finished") });
        onMerged?.();
      }
    }, 2000);
    return () => clearInterval(tmr);
  }, [merging, t, onMerged]);

  function confirmMerge(sourceCount, name, survivorIds) {
    modals.openConfirmModal({
      title: t("company_dedup_confirm_title"),
      centered: true,
      children: (
        <Text size="sm">
          {name
            ? t("company_dedup_confirm_group", { name, count: sourceCount })
            : t("company_dedup_confirm_all", { count: sourceCount })}
        </Text>
      ),
      labels: {
        confirm: name ? t("company_dedup_merge_group") : t("company_dedup_merge_all"),
        cancel: t("cancel"),
      },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        try {
          await startCompanyDedup(survivorIds);
          notifications.show({ message: t("company_dedup_started") });
          await load();
        } catch {
          notifications.show({ color: "red", title: t("error"), message: t("error") });
        }
      },
    });
  }

  if (loading && !data) return null;

  const clusters = data?.clusters ?? [];
  const skipped = data?.skipped ?? [];
  const extra = clusters.reduce((n, c) => n + (c.sources?.length ?? 0), 0);
  const busy = jobIsActive(job);

  if (clusters.length === 0 && skipped.length === 0 && !merging) return null;

  return (
    <Card withBorder padding="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>{t("company_dedup_title")}</Title>
            {clusters.length > 0 && (
              <Text size="sm" c="dimmed" maw={560} mt={4}>
                {t("company_dedup_body", { groups: clusters.length, sources: extra })}
              </Text>
            )}
          </div>
          {clusters.length > 0 && (
            <Button
              size="xs"
              color="red"
              variant="light"
              leftSection={<IconGitMerge size={14} />}
              disabled={busy}
              onClick={() => confirmMerge(extra, null, null)}
            >
              {t("company_dedup_merge_all")}
            </Button>
          )}
        </Group>

        {merging && (
          <Group gap="sm">
            <Loader size="sm" />
            <Text size="sm" c="dimmed">{t("company_dedup_running")}</Text>
          </Group>
        )}

        {clusters.map((c) => (
          <div key={c.key} style={{ borderTop: "1px solid var(--mantine-color-default-border)", paddingTop: 12 }}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" gap="md">
              <Stack gap={6} style={{ minWidth: 0, flex: 1 }}>
                <DedupMember row={c.survivor} keep t={t} onOpen={() => setLocation(`/companies/${c.survivor.id}`)} />
                <Text size="xs" c="dimmed">{t("company_dedup_absorb")}</Text>
                {c.sources.map((s) => (
                  <DedupMember key={s.id} row={s} t={t} onOpen={() => setLocation(`/companies/${s.id}`)} />
                ))}
              </Stack>
              <Button
                size="xs"
                variant="default"
                disabled={busy}
                onClick={() => confirmMerge(c.sources.length, c.survivor.name, [c.survivor.id])}
              >
                {t("company_dedup_merge_group")}
              </Button>
            </Group>
          </div>
        ))}

        {skipped.length > 0 && (
          <Alert variant="light" color="yellow" title={t("company_dedup_skipped")}>
            <Stack gap={6}>
              {skipped.map((c) => (
                <Text key={c.key} size="sm">
                  {[c.survivor, ...(c.sources ?? [])].map((m) => m.name).join(" · ")}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}
      </Stack>
    </Card>
  );
}

function DedupMember({ row, keep, t, onOpen }) {
  return (
    <Group gap="xs" wrap="nowrap">
      <CompanyLogo companyId={row.id} companyName={row.name} size={22} color="gray" style={{ flexShrink: 0 }} />
      <Anchor size="sm" fw={keep ? 600 : 400} onClick={onOpen} style={{ cursor: "pointer" }}>{row.name}</Anchor>
      {keep && <Badge size="xs" variant="light">{t("company_dedup_keep")}</Badge>}
      {row.hasLogo && <Badge size="xs" variant="outline" color="gray">{t("company_dedup_has_logo")}</Badge>}
      <Text size="xs" c="dimmed">{t("company_dedup_people", { count: row.peopleCount ?? 0 })}</Text>
    </Group>
  );
}
